import { BaseComponent, ComponentConfig } from './base-component';
import { triggerEvent } from './util/event-handler';

const CLASS_SHOW = 'show';
const CLASS_HIGHLIGHTED = 'highlighted';

export class Dropdown extends BaseComponent {
  static get NAME() { return 'dropdown'; }

  static DefaultConfig: ComponentConfig = { onSelect: null };

  private _outsideClickHandler: ((e: Event) => void) | null = null;
  private _outsideClickTimer: ReturnType<typeof setTimeout> | null = null;
  private _dotNetRef: any = null;
  private _itemClickHandler: ((e: Event) => void) | null = null;
  private _keydownHandler: ((e: Event) => void) | null = null;

  constructor(element: Element, config: ComponentConfig = {}) {
    super(element, config);

    this._itemClickHandler = (e: Event) => {
      const item = (e.target as Element).closest('.dropdown-item:not(.disabled)');
      if (!item || !this._element?.contains(item)) return;
      const value = (item as HTMLElement).dataset.value ?? '';
      triggerEvent(this._element, 'antumo:dropdown:select', { value, item }, false);
      if (typeof this._config.onSelect === 'function') {
        (this._config.onSelect as Function)(item, value);
      }
      if (this._dotNetRef) {
        this._dotNetRef.invokeMethodAsync('OnItemSelected', value);
      }
      this.hide();
    };
    this._element!.addEventListener('click', this._itemClickHandler);

    this._keydownHandler = (e: Event) => {
      if (!(e instanceof KeyboardEvent)) return;
      const items = Array.from(
        this._element!.querySelectorAll('.dropdown-item:not(.disabled)')
      );
      if (!items.length) return;

      const currentIndex = items.findIndex(item =>
        item.classList.contains(CLASS_HIGHLIGHTED)
      );

      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          if (!this.isOpen()) { this.show(); return; }
          this._highlightItem(items, currentIndex < items.length - 1 ? currentIndex + 1 : 0);
          break;
        case 'ArrowUp':
          e.preventDefault();
          if (!this.isOpen()) { this.show(); return; }
          this._highlightItem(items, currentIndex > 0 ? currentIndex - 1 : items.length - 1);
          break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!this.isOpen()) { this.show(); return; }
          if (currentIndex >= 0) (items[currentIndex] as HTMLElement).click();
          break;
        case 'Escape':
          if (this.isOpen()) { e.preventDefault(); this.hide(); }
          break;
        case 'Home':
          e.preventDefault();
          if (this.isOpen()) this._highlightItem(items, 0);
          break;
        case 'End':
          e.preventDefault();
          if (this.isOpen()) this._highlightItem(items, items.length - 1);
          break;
      }
    };
    this._element!.addEventListener('keydown', this._keydownHandler);
  }

  isOpen(): boolean {
    return !!this._element?.classList.contains(CLASS_SHOW);
  }

  toggle(trigger?: Element): void {
    if (this.isOpen()) {
      this.hide();
    } else {
      this.show(trigger);
    }
  }

  show(trigger?: Element): void {
    if (!this._element || this.isOpen()) return;

    this._element.classList.add(CLASS_SHOW);
    trigger?.setAttribute('aria-expanded', 'true');

    const handler = (e: Event): void => {
      const target = e.target as Node;
      if (!this._element?.contains(target) && !trigger?.contains(target)) {
        this.hide();
        trigger?.setAttribute('aria-expanded', 'false');
      }
    };
    this._outsideClickHandler = handler;
    // Registered on the next tick so the opening click does not close it at once. hide() or
    // dispose() before that tick cancels it; before this, the timer still registered the
    // handler (or `null`) after close, leaving a dead document listener behind.
    this._outsideClickTimer = setTimeout(() => {
      this._outsideClickTimer = null;
      if (this._outsideClickHandler === handler) document.addEventListener('click', handler);
    }, 0);
  }

  hide(): void {
    if (!this._element || !this.isOpen()) return;

    this._element.classList.remove(CLASS_SHOW);
    this._clearHighlight();
    this._removeOutsideClick();
  }

  private _removeOutsideClick(): void {
    if (this._outsideClickTimer !== null) {
      clearTimeout(this._outsideClickTimer);
      this._outsideClickTimer = null;
    }
    if (this._outsideClickHandler) {
      document.removeEventListener('click', this._outsideClickHandler);
      this._outsideClickHandler = null;
    }
  }

  setDotNetRef(dotNetRef: any): void {
    this._dotNetRef = dotNetRef;
  }

  clearDotNetRef(): void {
    this._dotNetRef = null;
  }

  dispose(): void {
    if (this._keydownHandler && this._element) {
      this._element.removeEventListener('keydown', this._keydownHandler);
      this._keydownHandler = null;
    }
    if (this._itemClickHandler && this._element) {
      this._element.removeEventListener('click', this._itemClickHandler);
      this._itemClickHandler = null;
    }
    this.clearDotNetRef();
    this.hide();
    // Also when the menu is no longer marked open (element re-rendered by the framework).
    this._removeOutsideClick();
    super.dispose();
  }

  private _highlightItem(items: Element[], index: number): void {
    this._clearHighlight();
    if (index >= 0 && index < items.length) {
      items[index].classList.add(CLASS_HIGHLIGHTED);
      (items[index] as HTMLElement).scrollIntoView({ block: 'nearest' });
      const trigger = document.querySelector(
        `[data-antumo-target="#${this._element!.id}"]`
      );
      if (trigger) {
        trigger.setAttribute('aria-activedescendant', items[index].id || '');
      }
    }
  }

  private _clearHighlight(): void {
    if (!this._element) return;
    const highlighted = this._element.querySelectorAll(`.${CLASS_HIGHLIGHTED}`);
    for (const el of highlighted) {
      el.classList.remove(CLASS_HIGHLIGHTED);
    }
  }

  // ── Static helpers for Blazor interop ──────────────────────────────

  static initWithDotNetRef(elementId: string, dotNetRef: any): void {
    const el = document.getElementById(elementId);
    if (!el) return;
    const instance = Dropdown.getOrCreateInstance(el) as Dropdown;
    instance.setDotNetRef(dotNetRef);
  }

  static disposeById(elementId: string): void {
    const el = document.getElementById(elementId);
    if (!el) return;
    const instance = Dropdown.getInstance(el) as Dropdown | null;
    if (instance) instance.dispose();
  }
}
